import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { artistsAPI, rankingsAPI } from '../services/api';
import VideoCard from '../components/VideoCard';

const StatCard = ({ testId, label, value, accent }) => (
  <div data-testid={testId} className="card p-4 text-center">
    <p className="text-gray-500 text-xs uppercase tracking-widest mb-1 font-bold">{label}</p>
    <p className={`text-2xl md:text-3xl font-black ${accent ? 'text-brand-yellow' : 'text-white'}`}>
      {value}
    </p>
  </div>
);

const ProfileSkeleton = () => (
  <div data-testid="profile-skeleton" className="min-h-screen">
    {/* Hero skeleton */}
    <div className="relative py-12 px-4">
      <div className="max-w-7xl mx-auto flex flex-col items-center gap-4">
        <div className="w-28 h-28 rounded-full bg-gray-800/60 relative overflow-hidden">
          <div className="skeleton-shimmer absolute inset-0" />
        </div>
        <div className="h-8 w-48 rounded bg-gray-800/60 relative overflow-hidden">
          <div className="skeleton-shimmer absolute inset-0" />
        </div>
        <div className="h-6 w-24 rounded-full bg-gray-800/60 relative overflow-hidden">
          <div className="skeleton-shimmer absolute inset-0" />
        </div>
      </div>
    </div>
    {/* Stats skeleton */}
    <div className="max-w-3xl mx-auto px-4 grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="card p-4">
          <div className="h-4 w-16 mx-auto rounded bg-gray-800/60 relative overflow-hidden mb-2">
            <div className="skeleton-shimmer absolute inset-0" />
          </div>
          <div className="h-8 w-20 mx-auto rounded bg-gray-800/60 relative overflow-hidden">
            <div className="skeleton-shimmer absolute inset-0" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const ArtistProfile = () => {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [videos, setVideos] = useState([]);
  const [ranking, setRanking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArtist();
    fetchRanking();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchArtist = async () => {
    try {
      const response = await artistsAPI.getById(id);
      setArtist(response.data.artist);
      setVideos(response.data.videos);
    } catch (error) {
      console.error('Error fetching artist:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRanking = async () => {
    try {
      const response = await rankingsAPI.getAll({ artist_id: id });
      const data = response.data.rankings || [];
      if (data.length > 0) {
        setRanking(data[0]);
      }
    } catch (error) {
      // Rankings may not exist yet
      console.error('Error fetching ranking:', error);
    }
  };

  if (loading) {
    return <ProfileSkeleton />;
  }

  if (!artist) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-2">Artist not found</h2>
          <p className="text-gray-500">This artist may have been removed.</p>
        </div>
      </div>
    );
  }

  const streakDisplay = () => {
    if (!ranking || ranking.current_streak === 0) return '-';
    if (ranking.current_streak > 0) return `W${ranking.current_streak}`;
    return `L${Math.abs(ranking.current_streak)}`;
  };

  const streakColor = () => {
    if (!ranking || ranking.current_streak === 0) return '';
    return ranking.current_streak > 0 ? 'text-green-400' : 'text-red-400';
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-12 md:py-16 px-4">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(74,93,58,0.12)_0%,transparent_60%)]" />
        <div className="relative max-w-7xl mx-auto">
          <div className="flex flex-col items-center text-center">
            {/* Avatar */}
            {artist.avatar_url ? (
              <div className="relative mb-4">
                <div className="absolute -inset-1 rounded-full bg-gradient-to-br from-army/50 to-transparent blur-sm" />
                <img
                  src={artist.avatar_url}
                  alt={artist.display_name || artist.username}
                  className="relative w-28 h-28 md:w-36 md:h-36 rounded-full border-4 border-army/40 object-cover"
                  data-testid="artist-avatar"
                />
              </div>
            ) : (
              <div className="w-28 h-28 md:w-36 md:h-36 rounded-full bg-army/20 border-4 border-army/40 flex items-center justify-center text-brand-yellow text-5xl md:text-6xl font-black mb-4">
                {(artist.display_name || artist.username).charAt(0).toUpperCase()}
              </div>
            )}

            {/* Name */}
            <h1
              className="font-display text-3xl md:text-5xl font-black uppercase tracking-tight text-white mb-3"
              data-testid="artist-name"
            >
              {artist.display_name || artist.username}
            </h1>

            {/* Rank Badge */}
            {ranking && (
              <span
                data-testid="rank-badge"
                className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 text-brand-yellow text-sm font-bold uppercase tracking-wider"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
                #{ranking.rank} Ranked
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Stats Cards */}
      {ranking && (
        <section className="max-w-3xl mx-auto px-4 mb-10 fade-in-content">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            <StatCard
              testId="stat-elo"
              label="ELO Rating"
              value={ranking.elo_rating}
              accent
            />
            <StatCard
              testId="stat-record"
              label="Record"
              value={`${ranking.wins}-${ranking.losses}`}
            />
            <StatCard
              testId="stat-winrate"
              label="Win Rate"
              value={`${ranking.win_percentage}%`}
            />
            <div data-testid="stat-streak" className="card p-4 text-center">
              <p className="text-gray-500 text-xs uppercase tracking-widest mb-1 font-bold">Streak</p>
              <p className={`text-2xl md:text-3xl font-black ${streakColor() || 'text-gray-500'}`}>
                {streakDisplay()}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Battles Grid */}
      <section className="max-w-7xl mx-auto px-4 pb-16">
        <h2 className="section-title text-white text-2xl md:text-3xl mb-6">
          ALL <span className="text-brand-yellow">BATTLES</span>
        </h2>

        {videos.length > 0 ? (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 fade-in-content"
            data-testid="artist-videos"
          >
            {videos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <p className="text-gray-500">No battles available for this artist yet.</p>
          </div>
        )}
      </section>
    </div>
  );
};

export default ArtistProfile;
